import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../api';

function toDate(value) {
  if (!value) return null;
  if (value && typeof value === 'object' && '_seconds' in value) return new Date(value._seconds * 1000);
  return new Date(value);
}

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive | trial
  const [showRequestedOnly, setShowRequestedOnly] = useState(false);
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview | new-users | subscribed | trial-quota-full | trial-quota-ending

  const [editing, setEditing] = useState(null); // user being edited
  const [form, setForm] = useState({ plan: 'trial', status: 'active', months: 12, endsAt: '', active: true });
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  
  // Delete user state
  const [deleting, setDeleting] = useState(null); // user being deleted
  const [deletePreview, setDeletePreview] = useState(null); // preview of what will be deleted
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Quota management state
  const [quotaModal, setQuotaModal] = useState(null); // user for quota management
  const [quotaData, setQuotaData] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaForm, setQuotaForm] = useState({ customLimit: '', reason: '', expiresAt: '' });


  const load = async () => {
    setError('');
    setLoading(true);
    try {
      const list = await adminApi.listUsers();
      setUsers(list || []);
      
      // Load quota data for trial users
      await loadQuotaData(list || []);
    } catch (e) {
      setError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadQuotaData = async (userList) => {
    setQuotaLoading(true);
    try {
      // Load quota data for trial users in parallel
      const quotaPromises = userList
        .filter(user => (user.plan || '').toLowerCase() === 'trial')
        .map(async (user) => {
          try {
            const quotaData = await adminApi.getUserQuota(user.userId);
            return { userId: user.userId, quotaData };
          } catch (error) {
            console.error(`Failed to load quota for user ${user.userId}:`, error);
            return { userId: user.userId, quotaData: null };
          }
        });
      
      const quotaResults = await Promise.all(quotaPromises);
      
      // Update users with quota data
      setUsers(prevUsers => 
        prevUsers.map(user => {
          const quotaResult = quotaResults.find(r => r.userId === user.userId);
          if (quotaResult && quotaResult.quotaData) {
            return {
              ...user,
              quotaData: quotaResult.quotaData,
              quotaExceeded: quotaResult.quotaData.quota?.quotaExceeded || false,
              quotaUsage: quotaResult.quotaData.quota?.currentUsage || 0,
              quotaLimit: quotaResult.quotaData.quota?.limit || 0
            };
          }
          return user;
        })
      );
    } catch (error) {
      console.error('Failed to load quota data:', error);
    } finally {
      setQuotaLoading(false);
    }
  };

  const loadSubscriptionPlans = async () => {
    setPlansLoading(true);
    try {
      const response = await adminApi.getSubscriptionPlans();
      const plansArray = Object.values(response.plans || {});
      setSubscriptionPlans(plansArray);
    } catch (e) {
      console.error('Failed to load subscription plans:', e);
      // Set fallback plans if API fails
      setSubscriptionPlans([
        { id: 'trial', name: 'Trial', description: 'Free trial with limited features' },
        { id: 'pro', name: 'Pro', description: 'Everything unlimited' },
        { id: 'pro3year', name: 'Pro 3-Year', description: 'Best value for long-term growth' }
      ]);
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => { 
    load(); 
    loadSubscriptionPlans(); 
  }, []);

  const openEdit = (u) => {
    setEditing(u);
    
    // Use new subscription status format from admin API
    const currentPlan = u.plan || 'trial';
    const currentStatus = u.subscriptionStatus || 'active';
    const currentEndsAt = u.subscriptionEndsAt;
    
    // Format current expiry date for datetime-local input
    let formattedEndsAt = '';
    if (currentEndsAt) {
      const date = toDate(currentEndsAt);
      if (date) {
        // Format for datetime-local input (YYYY-MM-DDTHH:MM)
        formattedEndsAt = date.toISOString().slice(0, 16);
      }
    }
    
    setForm({
      plan: currentPlan.toLowerCase(),
      status: currentStatus.toLowerCase(),
      months: 0, // Default to 0 to indicate no expiry change
      endsAt: '', // Start with empty to avoid accidental expiry changes
      active: !!u.active,
    });
  };
  const closeEdit = () => { setEditing(null); };

  const saveEdit = async () => {
    if (!editing) return;
    const payload = { plan: form.plan, status: form.status, active: !!form.active };
    
    // Only include expiry changes if explicitly set
    if (form.endsAt && form.endsAt.trim() !== '') {
      // User has set a specific expiry date
      payload.endsAt = new Date(form.endsAt).toISOString();
      payload.months = 0;
    } else if (form.months && Number(form.months) > 0) {
      // User wants to extend by months
      payload.months = Number(form.months);
    }
    // If neither endsAt nor months is set, don't modify expiry
    
    await adminApi.updateSubscription(editing.userId, payload);
    setEditing(null);
    await load();
  };

  const setActive = async (userId, active) => {
    await (active ? adminApi.activateUser(userId) : adminApi.deactivateUser(userId));
    await load();
  };

  const openDelete = async (user) => {
    setDeleting(user);
    setDeleteLoading(true);
    try {
      const preview = await adminApi.getUserDeletePreview(user.userId);
      setDeletePreview(preview);
    } catch (e) {
      setError(e?.message || 'Failed to load deletion preview');
    } finally {
      setDeleteLoading(false);
    }
  };

  const closeDelete = () => {
    setDeleting(null);
    setDeletePreview(null);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await adminApi.deleteUser(deleting.userId);
      setDeleting(null);
      setDeletePreview(null);
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openQuotaModal = async (user) => {
    setQuotaModal(user);
    setQuotaLoading(true);
    try {
      const data = await adminApi.getUserQuota(user.userId);
      setQuotaData(data);
    } catch (e) {
      setError(e?.message || 'Failed to load quota data');
    } finally {
      setQuotaLoading(false);
    }
  };

  const closeQuotaModal = () => {
    setQuotaModal(null);
    setQuotaData(null);
    setQuotaForm({ customLimit: '', reason: '', expiresAt: '' });
  };

  const setQuotaOverride = async () => {
    if (!quotaModal || !quotaForm.customLimit) return;
    setQuotaLoading(true);
    try {
      await adminApi.setUserQuotaOverride(quotaModal.userId, {
        customLimit: parseInt(quotaForm.customLimit),
        reason: quotaForm.reason,
        expiresAt: quotaForm.expiresAt || null
      });
      setQuotaForm({ customLimit: '', reason: '', expiresAt: '' });
      await openQuotaModal(quotaModal); // Refresh quota data
    } catch (e) {
      setError(e?.message || 'Failed to set quota override');
    } finally {
      setQuotaLoading(false);
    }
  };

  const removeQuotaOverride = async () => {
    if (!quotaModal) return;
    setQuotaLoading(true);
    try {
      await adminApi.removeUserQuotaOverride(quotaModal.userId);
      await openQuotaModal(quotaModal); // Refresh quota data
    } catch (e) {
      setError(e?.message || 'Failed to remove quota override');
    } finally {
      setQuotaLoading(false);
    }
  };

  // Compute dashboard statistics
  const dashboardStats = useMemo(() => {
    const now = new Date();
    const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const stats = {
      totalUsers: users.length,
      newUsersLast7Days: 0,
      newUsersLast30Days: 0,
      subscribedUsers: 0,
      trialUsers: 0,
      trialUsersQuotaFull: 0,
      trialUsersQuotaEnding: 0,
      activeUsers: 0,
      inactiveUsers: 0
    };

    users.forEach(user => {
      const createdAt = toDate(user.createdAt);
      const endsAt = toDate(user.subscriptionEndsAt);
      const isTrial = (user.plan || '').toLowerCase() === 'trial';
      const isSubscribed = !isTrial && user.valid !== false;
      const isActive = !!user.active;
      
      // Count new users
      if (createdAt && createdAt >= last7Days) stats.newUsersLast7Days++;
      if (createdAt && createdAt >= last30Days) stats.newUsersLast30Days++;
      
      // Count user types
      if (isSubscribed) stats.subscribedUsers++;
      if (isTrial) stats.trialUsers++;
      if (isActive) stats.activeUsers++;
      if (!isActive) stats.inactiveUsers++;
      
      // Check trial quota status
      if (isTrial) {
        // Check if quota is exceeded
        if (user.quotaExceeded) {
          stats.trialUsersQuotaFull++;
        }
        
        // Check if quota is ending soon (within 7 days) or subscription is ending
        const quotaEndingSoon = user.quotaUsage && user.quotaLimit && 
          (user.quotaUsage / user.quotaLimit) >= 0.8; // 80% or more usage
        const subscriptionEndingSoon = endsAt && endsAt <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        if (quotaEndingSoon || subscriptionEndingSoon) {
          stats.trialUsersQuotaEnding++;
        }
      }
    });

    return stats;
  }, [users]);

  // Filter users for different sections
  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = new Date();
    const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    return (users || []).filter((u) => {
      const requested = (u.subscriptionRequestStatus === 'pending') || !!u.subscriptionRequestAt;
      const endsAt = toDate(u.subscriptionEndsAt);
      const expired = !!endsAt && endsAt <= now;
      const createdAt = toDate(u.createdAt);
      const isTrial = (u.plan || '').toLowerCase() === 'trial';
      const isSubscribed = !isTrial && u.valid !== false;
      
      if (q) {
        const hay = `${u.name || ''} ${u.email || ''} ${u.phone || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      
      // Filter by active tab
      if (activeTab === 'new-users') {
        return createdAt && createdAt >= last7Days;
      } else if (activeTab === 'subscribed') {
        return isSubscribed;
      } else if (activeTab === 'trial-quota-full') {
        return isTrial && u.quotaExceeded;
      } else if (activeTab === 'trial-quota-ending') {
        const quotaEndingSoon = u.quotaUsage && u.quotaLimit && 
          (u.quotaUsage / u.quotaLimit) >= 0.8; // 80% or more usage
        const subscriptionEndingSoon = endsAt && endsAt <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        return isTrial && (quotaEndingSoon || subscriptionEndingSoon);
      }
      
      // Default filtering for overview
      if (statusFilter !== 'all') {
        if (statusFilter === 'active' && !u.active) return false;
        if (statusFilter === 'inactive' && !!u.active) return false;
        if (statusFilter === 'trial' && !isTrial) return false;
      }
      if (showRequestedOnly && !requested) return false;
      if (showExpiredOnly && !expired) return false;
      return true;
    }).map((u) => {
      const endsAt = toDate(u.subscriptionEndsAt);
      const expired = !!endsAt && endsAt <= new Date();
      const requested = (u.subscriptionRequestStatus === 'pending') || !!u.subscriptionRequestAt;
      const createdAt = toDate(u.createdAt);
      const isTrial = (u.plan || '').toLowerCase() === 'trial';
      const isSubscribed = !isTrial && u.valid !== false;
      
      return { 
        ...u, 
        _endsAt: endsAt, 
        _expired: expired, 
        _requested: requested,
        _createdAt: createdAt,
        _isTrial: isTrial,
        _isSubscribed: isSubscribed
      };
    });
  }, [users, query, statusFilter, showRequestedOnly, showExpiredOnly, activeTab]);

  const filtered = filteredUsers;

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-bold text-green-700">Admin – Users & Subscriptions</div>
        <button
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
          onClick={load}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Dashboard Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-green-100 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-700">{dashboardStats.totalUsers}</div>
          <div className="text-sm text-gray-600">Total Users</div>
        </div>
        <div className="bg-white border border-green-100 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-700">{dashboardStats.newUsersLast7Days}</div>
          <div className="text-sm text-gray-600">New (7 days)</div>
        </div>
        <div className="bg-white border border-green-100 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-700">{dashboardStats.subscribedUsers}</div>
          <div className="text-sm text-gray-600">Subscribed</div>
        </div>
        <div className="bg-white border border-green-100 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-700">{dashboardStats.trialUsers}</div>
          <div className="text-sm text-gray-600">Trial Users</div>
          {quotaLoading && (
            <div className="text-xs text-gray-500 mt-1">Loading quota data...</div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border border-green-100 rounded-xl mb-4">
        <div className="flex flex-wrap gap-2 p-3">
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'overview' 
                ? 'bg-green-100 text-green-700 border border-green-300' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'new-users' 
                ? 'bg-green-100 text-green-700 border border-green-300' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('new-users')}
          >
            New Users ({dashboardStats.newUsersLast7Days})
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'subscribed' 
                ? 'bg-green-100 text-green-700 border border-green-300' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('subscribed')}
          >
            Subscribed ({dashboardStats.subscribedUsers})
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'trial-quota-full' 
                ? 'bg-green-100 text-green-700 border border-green-300' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('trial-quota-full')}
          >
            Trial Quota Full ({dashboardStats.trialUsersQuotaFull})
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'trial-quota-ending' 
                ? 'bg-green-100 text-green-700 border border-green-300' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('trial-quota-ending')}
          >
            Trial Quota Ending ({dashboardStats.trialUsersQuotaEnding})
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-green-100 rounded-xl p-3 mb-4">
        <div className="flex flex-col gap-3">
          <input
            className="border border-green-200 rounded px-3 py-2 text-sm w-full"
            placeholder="Search by name, email, phone"
            value={query}
            onChange={(e)=>setQuery(e.target.value)}
          />
          {activeTab === 'overview' && (
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <select className="border border-green-200 rounded px-3 py-2 text-sm flex-1" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}>
                <option value="all">All Users</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="trial">Trial</option>
              </select>
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="flex items-center gap-2 text-xs text-gray-600 px-2 py-1 bg-gray-50 rounded">
                  <input type="checkbox" checked={showRequestedOnly} onChange={(e)=>setShowRequestedOnly(e.target.checked)} /> 
                  <span>Requested</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 px-2 py-1 bg-gray-50 rounded">
                  <input type="checkbox" checked={showExpiredOnly} onChange={(e)=>setShowExpiredOnly(e.target.checked)} /> 
                  <span>Expired</span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
      
      {/* Section Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {activeTab === 'overview' && 'All Users'}
          {activeTab === 'new-users' && 'New Users (Last 7 Days)'}
          {activeTab === 'subscribed' && 'Subscribed Users'}
          {activeTab === 'trial-quota-full' && 'Trial Users with Full Quota'}
          {activeTab === 'trial-quota-ending' && 'Trial Users with Quota Ending Soon'}
        </h3>
        <p className="text-sm text-gray-600">
          {filtered.length} user{filtered.length !== 1 ? 's' : ''} found
        </p>
      </div>

      <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm">
        {loading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((u) => (
              <div
                key={u.userId}
                className={`border rounded-lg p-4 ${
                  u._requested ? 'bg-green-50 border-green-300' : 
                  u._expired ? 'bg-red-50 border-red-300' : 
                  'bg-white border-gray-200'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* User Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="font-medium text-gray-900 text-base">
                        {u.name}
                      </div>
                      {u._isTrial && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                          Trial
                        </span>
                      )}
                      {u._isSubscribed && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          Subscribed
                        </span>
                      )}
                      {u._requested && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          Requested
                        </span>
                      )}
                      {u._expired && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                          Expired
                        </span>
                      )}
                      {u.quotaExceeded && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                          Quota Full
                        </span>
                      )}
                      {u.quotaUsage && u.quotaLimit && (u.quotaUsage / u.quotaLimit) >= 0.8 && !u.quotaExceeded && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                          Quota High
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Contact:</span> {u.email || u.phone || '—'}
                      </div>
                      <div>
                        <span className="font-medium">Plan:</span> {u.planName || u.plan || '—'}
                      </div>
                      <div>
                        <span className="font-medium">Status:</span> {u.subscriptionStatus || '—'}
                      </div>
                      <div>
                        <span className="font-medium">Active:</span> {u.active ? 'Yes' : 'No'}
                      </div>
                      {u._createdAt && (
                        <div>
                          <span className="font-medium">Joined:</span> {u._createdAt.toLocaleDateString()}
                        </div>
                      )}
                      {u._endsAt && (
                        <div>
                          <span className="font-medium">Expires:</span> {u._endsAt.toLocaleDateString()}
                        </div>
                      )}
                      {u.quotaUsage !== undefined && u.quotaLimit !== undefined && (
                        <div>
                          <span className="font-medium">Quota:</span> {u.quotaUsage}/{u.quotaLimit === -1 ? '∞' : u.quotaLimit}
                          {u.quotaLimit !== -1 && (
                            <span className="ml-2 text-xs">
                              ({Math.round((u.quotaUsage / u.quotaLimit) * 100)}%)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex gap-2">
                      <button 
                        className={`px-3 py-1 rounded text-white text-xs ${u.active ? 'bg-gray-300' : 'bg-green-600'}`} 
                        disabled={u.active} 
                        onClick={()=>setActive(u.userId, true)}
                      >
                        Activate
                      </button>
                      <button 
                        className={`px-3 py-1 rounded text-white text-xs ${!u.active ? 'bg-gray-300' : 'bg-gray-600'}`} 
                        disabled={!u.active} 
                        onClick={()=>setActive(u.userId, false)}
                      >
                        Deactivate
                      </button>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button className="px-3 py-1 rounded bg-green-700 text-white text-xs" onClick={()=>openEdit(u)}>
                        Edit Subscription
                      </button>
                      <button className="px-3 py-1 rounded bg-blue-600 text-white text-xs" onClick={()=>openQuotaModal(u)}>
                        Manage Quota
                      </button>
                      <button className="px-3 py-1 rounded bg-red-600 text-white text-xs" onClick={()=>openDelete(u)}>
                        Delete User
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {filtered.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-lg mb-2">No users found</div>
                <div className="text-sm">
                  {activeTab === 'new-users' && 'No new users in the last 7 days'}
                  {activeTab === 'subscribed' && 'No subscribed users found'}
                  {activeTab === 'trial-quota-full' && 'No trial users with full quota'}
                  {activeTab === 'trial-quota-ending' && 'No trial users with quota ending soon'}
                  {activeTab === 'overview' && 'Try adjusting your search or filters'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2">
          <div className="absolute inset-0 bg-black/20" onClick={closeEdit}></div>
          <div className="relative bg-white rounded-2xl border border-green-100 shadow-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="text-lg sm:text-xl font-semibold text-green-700 mb-4">
                Edit Subscription: {editing.name}
              </div>
            
            <div className="grid gap-4">
              {/* Current User Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-blue-900 mb-3">Current User Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-blue-800">Name:</span>
                    <span className="ml-2 text-blue-700">{editing.name}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">Email:</span>
                    <span className="ml-2 text-blue-700">{editing.email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">Phone:</span>
                    <span className="ml-2 text-blue-700">{editing.phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">Current Plan:</span>
                    <span className="ml-2 text-blue-700 font-semibold">
                      {editing.plan || 'Trial'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">Current Status:</span>
                    <span className="ml-2 text-blue-700">
                      {editing.subscriptionStatus || 'Active'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">User Active:</span>
                    <span className="ml-2 text-blue-700">{editing.active ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* User Status */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">User Status</h3>
                <div className="flex items-center gap-2">
                  <input id="activeToggle" type="checkbox" className="accent-green-600" checked={!!form.active} onChange={(e)=>setForm(s=>({...s, active: e.target.checked}))} />
                  <label htmlFor="activeToggle" className="text-sm text-gray-700">User Active</label>
                </div>
              </div>

              {/* Plan Selection */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-blue-900 mb-3">Subscription Plan</h3>
                <div className="grid gap-3">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Select New Plan</label>
                    {plansLoading ? (
                      <div className="text-sm text-gray-500">Loading plans...</div>
                    ) : (
                      <select className="w-full border border-blue-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={form.plan} onChange={(e)=>setForm(s=>({...s, plan: e.target.value}))}>
                        {subscriptionPlans.map(plan => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} - {plan.description}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  {/* Show selected plan details */}
                  {subscriptionPlans.length > 0 && (
                    <div className="bg-white border border-blue-200 rounded p-3">
                      {(() => {
                        const selectedPlan = subscriptionPlans.find(p => p.id === form.plan);
                        return selectedPlan ? (
                          <div className="text-sm">
                            <div className="font-medium text-blue-900 mb-1">{selectedPlan.name}</div>
                            <div className="text-blue-700 mb-2">{selectedPlan.description}</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-blue-600">
                              <div>Price: ₹{selectedPlan.price} {selectedPlan.currency}</div>
                              <div>Duration: {selectedPlan.duration === 0 ? 'No expiry' : `${selectedPlan.duration} days`}</div>
                            </div>
                            {selectedPlan.features && selectedPlan.features.length > 0 && (
                              <div className="mt-2">
                                <div className="font-medium text-blue-800">Features:</div>
                                <ul className="list-disc list-inside text-xs text-blue-600 mt-1">
                                  {selectedPlan.features.slice(0, 3).map((feature, idx) => (
                                    <li key={idx}>{feature}</li>
                                  ))}
                                  {selectedPlan.features.length > 3 && <li>...and more</li>}
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-yellow-900 mb-2">Subscription Status</h3>
                <div>
                  <label className="block text-sm font-medium text-yellow-800 mb-1">Status</label>
                  <select className="w-full border border-yellow-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500" value={form.status} onChange={(e)=>setForm(s=>({...s, status: e.target.value}))}>
                    <option value="active">Active</option>
                    <option value="trial">Trial</option>
                    <option value="inactive">Inactive</option>
                    <option value="expired">Expired</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Expiry Management */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-green-900 mb-3">Expiry Management (Optional)</h3>
                <div className="text-sm text-green-700 mb-4 bg-green-100 border border-green-300 rounded p-2">
                  💡 <strong>Note:</strong> Leave expiry fields empty to keep the current expiry unchanged. Only fill these fields if you want to modify the subscription expiry.
                </div>
                
                <div className="grid gap-4">
                  {/* Current Expiry */}
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Current Expiry</label>
                    <input 
                      className="w-full border border-green-300 rounded px-3 py-2 text-sm bg-gray-50" 
                      value={editing ? (toDate(editing.subscriptionEndsAt)?.toLocaleString() || 'No expiry set') : '—'} 
                      readOnly 
                    />
                  </div>

                  {/* Set New Expiry */}
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">
                      <span>Set New Expiry (Date & Time) - Optional</span>
                      {form.endsAt && (
                        <button 
                          type="button" 
                          className="ml-2 text-xs text-green-700 underline hover:text-green-800" 
                          onClick={()=>setForm(s=>({...s, endsAt: ''}))}
                        >
                          Clear
                        </button>
                      )}
                    </label>
                    <input 
                      className="w-full border border-green-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" 
                      type="datetime-local" 
                      value={form.endsAt} 
                      onChange={(e)=>setForm(s=>({...s, endsAt: e.target.value}))} 
                      placeholder="Leave empty to keep current expiry"
                    />
                    <div className="text-xs text-green-600 mt-1">
                      Leave empty to keep current expiry unchanged
                    </div>
                  </div>

                  {/* Extend by Months */}
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Or Extend by (Months) - Optional</label>
                    <div className="flex gap-2">
                      <input 
                        className="flex-1 border border-green-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" 
                        type="number" 
                        min="0" 
                        max="120"
                        value={form.months} 
                        onChange={(e)=>setForm(s=>({...s, months: e.target.value}))} 
                        placeholder="Enter months (0 = no change)"
                      />
                      <button 
                        type="button"
                        className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        onClick={() => {
                          const months = parseInt(form.months) || 0;
                          if (months > 0) {
                            const currentDate = new Date();
                            const newDate = new Date(currentDate.getTime() + (months * 30 * 24 * 60 * 60 * 1000));
                            setForm(s => ({...s, endsAt: newDate.toISOString().slice(0, 16)}));
                          }
                        }}
                      >
                        Apply
                      </button>
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      Enter 0 to keep current expiry unchanged. If you set an expiry date above, months extension will be ignored.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 w-full sm:w-auto" onClick={closeEdit}>
                Cancel
              </button>
              <button className="px-4 py-2 text-sm rounded bg-green-600 text-white hover:bg-green-700 w-full sm:w-auto" onClick={saveEdit}>
                Save Changes
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/20" onClick={closeDelete}></div>
          <div className="relative bg-white rounded-2xl border border-red-100 shadow-lg w-11/12 sm:w-[600px] p-6">
            <div className="text-lg font-semibold text-red-700 mb-4">⚠️ Delete User: {deleting.name}</div>
            
            {deleteLoading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Loading deletion preview...</div>
              </div>
            ) : deletePreview ? (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-red-800 font-medium mb-2">⚠️ This action will permanently delete:</div>
                  <div className="text-sm text-red-700">
                    <div>• <strong>User Account:</strong> {deleting.name} ({deleting.email || deleting.phone})</div>
                    <div>• <strong>Total Items:</strong> {deletePreview.totalItems} records</div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-sm text-gray-700 font-medium mb-2">📊 Detailed Breakdown:</div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                    {deletePreview.associations.orders > 0 && <div>• Orders: {deletePreview.associations.orders}</div>}
                    {deletePreview.associations.expenses > 0 && <div>• Expenses: {deletePreview.associations.expenses}</div>}
                    {deletePreview.associations.customers > 0 && <div>• Customers: {deletePreview.associations.customers}</div>}
                    {deletePreview.associations.products > 0 && <div>• Products: {deletePreview.associations.products}</div>}
                    {deletePreview.associations.inventoryItems > 0 && <div>• Inventory Items: {deletePreview.associations.inventoryItems}</div>}
                    {deletePreview.associations.moneyAccounts > 0 && <div>• Money Accounts: {deletePreview.associations.moneyAccounts}</div>}
                    {deletePreview.associations.ledgerEntries > 0 && <div>• Ledger Entries: {deletePreview.associations.ledgerEntries}</div>}
                    {deletePreview.associations.whatsappSessions > 0 && <div>• WhatsApp Sessions: {deletePreview.associations.whatsappSessions}</div>}
                    {deletePreview.associations.usageLogs > 0 && <div>• Usage Logs: {deletePreview.associations.usageLogs}</div>}
                    {deletePreview.associations.orgs > 0 && <div>• Organizations: {deletePreview.associations.orgs}</div>}
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="text-sm text-yellow-800">
                    <strong>⚠️ Warning:</strong> This action cannot be undone. All user data, including orders, expenses, customers, and all associated records will be permanently deleted.
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button 
                    className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50" 
                    onClick={closeDelete}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50" 
                    onClick={confirmDelete}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'Deleting...' : 'Delete User & All Data'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-red-500">Failed to load deletion preview</div>
                <button className="mt-4 px-4 py-2 text-sm rounded border border-gray-300" onClick={closeDelete}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quota Management Modal */}
      {quotaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2">
          <div className="absolute inset-0 bg-black/20" onClick={closeQuotaModal}></div>
          <div className="relative bg-white rounded-2xl border border-blue-100 shadow-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
            <div className="text-lg font-semibold text-blue-700 mb-4">📊 Manage Quota: {quotaModal.name}</div>
            
            {quotaLoading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Loading quota information...</div>
              </div>
            ) : quotaData ? (
              <div className="space-y-6">
                {/* Current Quota Status */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-blue-900 mb-3">Current Quota Status</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-blue-800">Limit:</span>
                      <span className="ml-2 text-blue-700">
                        {quotaData.quota.isUnlimited ? 'Unlimited' : `${quotaData.quota.limit} total entries`}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-blue-800">Current Usage:</span>
                      <span className="ml-2 text-blue-700">
                        {quotaData.quota.breakdown ? 
                          `${quotaData.quota.breakdown.total} total (${quotaData.quota.breakdown.orders} orders + ${quotaData.quota.breakdown.expenses} expenses)` :
                          `${quotaData.quota.currentUsage} total entries`
                        }
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-blue-800">Remaining:</span>
                      <span className="ml-2 text-blue-700">
                        {quotaData.quota.isUnlimited ? 'Unlimited' : `${quotaData.quota.remainingQuota} entries`}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-blue-800">Status:</span>
                      <span className={`ml-2 ${quotaData.quota.quotaExceeded ? 'text-red-700' : 'text-green-700'}`}>
                        {quotaData.quota.quotaExceeded ? 'Exceeded' : 'Within Limit'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* User Information */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">User Information</h3>
                  <div className="text-sm text-gray-700">
                    <div><strong>Name:</strong> {quotaData.user.name}</div>
                    <div><strong>Email:</strong> {quotaData.user.email || 'N/A'}</div>
                    <div><strong>Phone:</strong> {quotaData.user.phone || 'N/A'}</div>
                    <div><strong>Subscription:</strong> {quotaData.user.subscriptionStatus || 'N/A'}</div>
                  </div>
                </div>

                {/* Current Custom Override */}
                {quotaData.customOverride && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h3 className="text-lg font-medium text-blue-900 mb-3">Current Custom Override</h3>
                    <div className="text-sm text-blue-800 space-y-1">
                      <div><strong>Custom Limit:</strong> {quotaData.customOverride.customLimit === -1 ? 'Unlimited' : quotaData.customOverride.customLimit}</div>
                      <div><strong>Reason:</strong> {quotaData.customOverride.reason}</div>
                      <div><strong>Created:</strong> {new Date(quotaData.customOverride.createdAt).toLocaleString()}</div>
                      <div><strong>Created By:</strong> {quotaData.customOverride.createdBy}</div>
                      {quotaData.customOverride.expiresAt && (
                        <div><strong>Expires:</strong> {new Date(quotaData.customOverride.expiresAt).toLocaleString()}</div>
                      )}
                      <div><strong>Status:</strong> 
                        <span className={`ml-1 px-2 py-1 rounded text-xs ${
                          quotaData.customOverride.isExpired ? 'bg-red-100 text-red-800' : 
                          quotaData.customOverride.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {quotaData.customOverride.isExpired ? 'Expired' : 
                           quotaData.customOverride.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Set Custom Quota Override */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-yellow-900 mb-3">
                    {quotaData.customOverride ? 'Update Custom Quota Override' : 'Set Custom Quota Override'}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-yellow-800 mb-1">Custom Limit</label>
                      <input
                        type="number"
                        min="0"
                        value={quotaForm.customLimit}
                        onChange={(e) => setQuotaForm({ ...quotaForm, customLimit: e.target.value })}
                        className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Enter custom limit (or -1 for unlimited)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-yellow-800 mb-1">Reason (Optional)</label>
                      <input
                        type="text"
                        value={quotaForm.reason}
                        onChange={(e) => setQuotaForm({ ...quotaForm, reason: e.target.value })}
                        className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Reason for override"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-yellow-800 mb-1">Expires At (Optional)</label>
                      <input
                        type="datetime-local"
                        value={quotaForm.expiresAt}
                        onChange={(e) => setQuotaForm({ ...quotaForm, expiresAt: e.target.value })}
                        className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <button
                      onClick={setQuotaOverride}
                      disabled={quotaLoading || !quotaForm.customLimit}
                      className="w-full px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                    >
                      {quotaLoading ? 'Setting...' : 'Set Custom Quota'}
                    </button>
                  </div>
                </div>

                {/* Remove Override */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-red-900 mb-2">Remove Custom Override</h3>
                  <p className="text-sm text-red-700 mb-3">
                    This will remove any custom quota override and revert to the default quota for this user type.
                  </p>
                  <button
                    onClick={removeQuotaOverride}
                    disabled={quotaLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {quotaLoading ? 'Removing...' : 'Remove Override'}
                  </button>
                </div>

                <div className="flex justify-end gap-3">
                  <button 
                    className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50" 
                    onClick={closeQuotaModal}
                    disabled={quotaLoading}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-red-500">Failed to load quota data</div>
                <button className="mt-4 px-4 py-2 text-sm rounded border border-gray-300" onClick={closeQuotaModal}>Close</button>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


